import { useState } from "react";
import { Flex, Typography } from "antd";
import { useParams } from "react-router-dom";
import { useNotification, useTranslate } from "@refinedev/core";
import { PlusIcon } from "@phosphor-icons/react";
import { serializeKeyPoints, Skeleton } from "@libertify/ui";
import type { SerializableKeyPoint, SummaryDocument } from "@libertify/ui";
import { PlaybookAddOnCard } from "../../PlaybookAddOnCard";
import { DashedAddButton } from "../../DashedAddButton";
import { CONFIG_ITEM } from "../../../data/addons";
import { KeyPointItem } from "./KeyPointItem";
import {
  newPointRow,
  resolveRow,
  titleUntitledPoints,
  toInitial,
  toWire,
  type KeyPointRow,
} from "./helpers";
import { useKeyPoints, useSaveKeyPoints } from "@libertify/experience-viewer";
import { API_URL } from "@/constants";
import { useAuth } from "hooks/useAuth";
import { useAssetListAutosave } from "../hooks/useAssetListAutosave";

const KEY = "experienceEdit.keyPointsCard";
export type KeyPointsEditCardProps = {
  visible?: boolean;
  onVisibleChange?: (visible: boolean) => void;
  visiblePending?: boolean;
};

/**
 * Edit the generated Key points of a published experience.
 *
 * Unlike the other edit cards, this one edits add-on *content* rather than
 * generation settings: the points are parsed out of the stored markdown and
 * written back as markdown (see `parseSummaryDocument`/`serializeKeyPoints`).
 */
export const KeyPointsEditCard = ({
  visible,
  onVisibleChange,
  visiblePending,
}: KeyPointsEditCardProps) => {
  const t = useTranslate();
  const { projectId } = useParams<{ projectId: string }>();
  // Same bearer the experience page hands the viewer's add-on reads.
  const { accessToken } = useAuth();
  const { assetId, document, isLoading, isError } = useKeyPoints({
    apiBaseUrl: API_URL,
    projectId: projectId ?? "",
    token: accessToken,
  });

  const renderBody = () => {
    if (isLoading) return <Skeleton variant="text" rows={6} />;
    if (isError) {
      return <Typography.Text type="danger">{t(`${KEY}.loadError`)}</Typography.Text>;
    }
    if (!assetId || !document) {
      return <Typography.Text type="secondary">{t(`${KEY}.empty`)}</Typography.Text>;
    }
    // Keyed on the asset: a regenerated summary is a new asset, and rows
    // seeded from the old one would be written over it.
    return (
      <KeyPointsForm
        key={assetId}
        loaded={document}
        assetId={assetId}
        projectId={projectId ?? ""}
        accessToken={accessToken}
      />
    );
  };

  return (
    <PlaybookAddOnCard
      itemId={CONFIG_ITEM.Summary}
      title={t(`${KEY}.title`)}
      description={t(`${KEY}.subtitle`)}
      visible={visible}
      visiblePending={visiblePending}
      onVisibleChange={onVisibleChange}
    >
      {renderBody()}
    </PlaybookAddOnCard>
  );
};

type KeyPointsFormProps = {
  loaded: SummaryDocument;
  assetId: string;
  projectId: string;
  accessToken: string | null | undefined;
};

/**
 * Mounted once the summary is in, and seeded once: the save writes the query
 * cache, so a remount picks up the edit without re-reading. Owns the autosave,
 * mirroring `QuizEditCard`.
 */
const KeyPointsForm = ({
  loaded,
  assetId,
  projectId,
  accessToken,
}: KeyPointsFormProps) => {
  const t = useTranslate();
  const { open } = useNotification();
  const { save } = useSaveKeyPoints({
    apiBaseUrl: API_URL,
    projectId,
    token: accessToken,
  });

  // The preamble and title dialect aren't editable; every save carries them
  // through unchanged.
  const [{ preamble, rows: initialRows }] = useState(() => toInitial(loaded));
  const [rows, setRows] = useState<KeyPointRow[]>(initialRows);
  // Only the point folded from a sectionless summary saves without a title.
  const [untitledSavedIds] = useState(
    () =>
      new Set(
        initialRows.filter((row) => !row.title.trim()).map((row) => row.id)
      )
  );

  const toMarkdown = (points: SerializableKeyPoint[]) =>
    serializeKeyPoints({
      preamble,
      style: loaded.style,
      points: titleUntitledPoints(points, t(`${KEY}.introTitle`)),
    });

  const persist = useAssetListAutosave<KeyPointRow, SerializableKeyPoint>({
    initialRows,
    saveKey: assetId,
    resolveRow,
    toWire,
    isSameWire: (a, b) => toMarkdown(a) === toMarkdown(b),
    save: (points) => save(assetId, toMarkdown(points)),
    onSaveFailed: () =>
      open?.({ type: "error", message: t(`${KEY}.saveFailed`) }),
    revert: setRows,
  });

  const update = (next: KeyPointRow[]) => {
    setRows(next);
    persist(next);
  };

  const updatePoint = (id: string, patch: Partial<KeyPointRow>) =>
    update(rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));

  return (
    <Flex vertical gap={12}>
      {rows.length === 0 ? (
        <Typography.Text type="secondary">{t(`${KEY}.noPoints`)}</Typography.Text>
      ) : null}
      {rows.map((row, index) => (
        <KeyPointItem
          key={row.id}
          index={index}
          title={row.title}
          body={row.body}
          needsTitle={
            !row.title.trim() &&
            Boolean(row.body.trim()) &&
            !untitledSavedIds.has(row.id)
          }
          onTitleChange={(title) => updatePoint(row.id, { title })}
          onBodyChange={(body) => updatePoint(row.id, { body })}
          onRemove={() => update(rows.filter(({ id }) => id !== row.id))}
        />
      ))}
      <DashedAddButton
        icon={<PlusIcon size={16} weight="bold" />}
        label={t(`${KEY}.addPoint`)}
        onClick={() => update([...rows, newPointRow()])}
        iconBordered={false}
        centered
      />
    </Flex>
  );
};

export default KeyPointsEditCard;
